<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { z } from 'zod'
import { displayErrorMessage, newsletterSubscribe } from '~/queries'

const { $toast, $i18n } = useNuxtApp()
const { t } = $i18n

const validationSchema = toTypedSchema(
  z.object({
    name: z.string().trim().min(1),
    email: z.string().trim().email(),
  }),
)

const { defineField, handleSubmit, errors, isSubmitting, resetForm } = useForm({
  validationSchema,
})

const { utmPayload } = useOrderUtm()
const { devicePayload } = useDeviceSignal()

const [name, nameAttrs] = defineField('name')
const [email, emailAttrs] = defineField('email')

const onSubmit = handleSubmit(async (values) => {
  try {
    await newsletterSubscribe({
      name: values.name,
      email: values.email,
      ...utmPayload(),
      ...(await devicePayload()),
    })

    $toast.success(t('feedback.newsletter.success'))

    resetForm()
  } catch (error) {
    displayErrorMessage(error)
  }
})
</script>

<template>
  <section id="app-newsletter-section" class="app-newsletter">
    <div class="row items-center">
      <div class="col-lg-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 178.01 333.01"
          class="app-newsletter__illustration"
          aria-hidden="true"
        >
          <path
            d="M111.54.09c8.8-.47,17.52,1,26.26,1.39,12,.47,21.64,7.21,28.62,16.78,11.59,15.91,15.17,33.32,7.61,52-4.22,10.43-13.48,15.65-23.27,19.8s-20.42,6-31.09,6.79c-3.52.24-5.24,1.57-6.41,4.78-3.47,9.59-4,19.81-6.67,29.54-.34,1.2-.63,2.41-.92,3.62-.48,2-.86,4.16-3.56,4.22s-3.21-2.09-3.56-4.12c-1.17-6.75-2.26-13.5-3.41-20.24-.65-3.83-1.43-7.64-2-11.47-.42-2.61-1.74-3.43-4.45-3.44A383.17,383.17,0,0,1,48.7,97.84,82.46,82.46,0,0,1,10,83.19C.89,76.88-1,67,.43,56.49c2-14.79,11.16-25.1,22.5-33.69C42.25,8.15,64.78,2.92,88.53,1.56c7.68-.43,15.34-1,23-1.46Zm-9.27,119.3c2.06-8.79,3.21-16.5,6.4-23.68,1.27-2.85,2.74-4.38,6.11-4.7,9-.84,18.12-1.78,26.81-4.57C163.92,79.27,173,68.9,170.81,47.28c-2.07-20.77-15.12-39.15-40-40.1-19.42-.75-38.92-1-58.21,2C52.73,12.35,34.49,19.26,19.71,33.44c-8.18,7.84-13.65,17-13.45,28.74.12,6.69,2,12.8,7.92,16.56A85.5,85.5,0,0,0,52.82,92.17C66,93.36,79.15,93.41,92.31,93.73c3.62.08,5.21,1.34,6.13,4.84a187.15,187.15,0,0,1,3.83,20.83Z"
            fill="#121318"
          />
          <path
            d="M33.22,69.84a10.27,10.27,0,0,1-4.14-.16,7,7,0,0,1-3.44-1.84,6.66,6.66,0,0,1-1.76-3.76,5.7,5.7,0,0,1,.29-3.31,4.66,4.66,0,0,1,1.76-1.93A6.25,6.25,0,0,1,28.1,58a8.13,8.13,0,0,0-1.22,2.77,7.41,7.41,0,0,0-.12,2.84,6,6,0,0,0,1,2.62A5,5,0,0,0,30.14,68a6.66,6.66,0,0,0,3.51.25A9.25,9.25,0,0,0,37,67a6.55,6.55,0,0,0,2.46-2.32,4.79,4.79,0,0,0,.6-3.36,4.91,4.91,0,0,0-1.32-2.78,9,9,0,0,0-2.58-1.81c-1-.49-2.05-.94-3.16-1.36s-2.18-.87-3.2-1.36a9.43,9.43,0,0,1-2.55-1.9,5.23,5.23,0,0,1-1.34-2.89,8.76,8.76,0,0,1,.24-3.59,13,13,0,0,1,1.71-3.78A11.91,11.91,0,0,1,31,38.69a10.6,10.6,0,0,1,4.49-1.75,4,4,0,0,1,3.41.64,4.3,4.3,0,0,1,1.34,2.65,10.43,10.43,0,0,1,0,3.15,21.25,21.25,0,0,1-.7,3.3c-.32,1-.64,1.94-1,2.69a1.71,1.71,0,0,1-1.12-.43A2.16,2.16,0,0,1,37,47.88a6.19,6.19,0,0,1,.05-2c.13-.74.28-1.52.45-2.32s.36-1.59.48-2.34a6.64,6.64,0,0,0,0-2,1.59,1.59,0,0,0-.67-1.15,2.49,2.49,0,0,0-1.8-.2,7,7,0,0,0-3.29,1.4A9.92,9.92,0,0,0,29.87,42a11.32,11.32,0,0,0-1.41,3.13,7.16,7.16,0,0,0-.2,2.81,4,4,0,0,0,1.22,2.39A9.67,9.67,0,0,0,32,51.85c1,.41,2,.83,3.1,1.25a22,22,0,0,1,3.16,1.4,8.39,8.39,0,0,1,2.55,2.1,6.37,6.37,0,0,1,1.4,3.25,7.28,7.28,0,0,1-.34,3.77,8.53,8.53,0,0,1-2,3,11.81,11.81,0,0,1-3.06,2.13A12.47,12.47,0,0,1,33.22,69.84Z"
            fill="#121318"
          />
          <path
            d="M48.94,65.09a2.56,2.56,0,0,1-2.35-.84,5.07,5.07,0,0,1-1.22-2.67,11.78,11.78,0,0,1-.06-2.7,29.78,29.78,0,0,1,.48-3.1,13.83,13.83,0,0,1,.77-2.58c.29-.69.59-1.06.87-1.1a1.81,1.81,0,0,1,.71.13,2.07,2.07,0,0,1,.88.43,17.62,17.62,0,0,0-.75,2.21A24,24,0,0,0,47.63,58a10.28,10.28,0,0,0,0,3,9.35,9.35,0,0,0,.24,1A2.55,2.55,0,0,0,48.42,63a1.05,1.05,0,0,0,1,.37,2.31,2.31,0,0,0,1.42-1,11.71,11.71,0,0,0,1.26-2.25,26.37,26.37,0,0,0,1.17-2.89c.35-1,.67-2,1-2.93s.57-1.71.77-2.34c.16,0,.39,0,.69.06a3.7,3.7,0,0,1,.88.18.63.63,0,0,1,.48.49,5.6,5.6,0,0,1-.23,1.13c-.17.62-.38,1.39-.61,2.31a27.3,27.3,0,0,0-.5,2.7,8.15,8.15,0,0,0,0,2.35,2.06,2.06,0,0,0,.45,1.09,1.2,1.2,0,0,0,1.09.28,4.05,4.05,0,0,0,1.9-1.06A11.41,11.41,0,0,0,61.18,59a16.35,16.35,0,0,0,1.63-3.8l.56.28a14.2,14.2,0,0,1-1.57,4.11,11.69,11.69,0,0,1-2.44,3,5.73,5.73,0,0,1-2.78,1.36,3.13,3.13,0,0,1-1.71-.14,2.34,2.34,0,0,1-1-.93,4.47,4.47,0,0,1-.46-1.42,6,6,0,0,1-.07-.95c0-.35,0-.7,0-1.06a21.64,21.64,0,0,1-1.16,2.68,8.66,8.66,0,0,1-1.45,2A3.17,3.17,0,0,1,48.94,65.09Z"
            fill="#121318"
          />
          <path
            d="M67.18,64a3.7,3.7,0,0,1-3.28-.86,6.13,6.13,0,0,1-1.55-3.47,20,20,0,0,1-.05-4.75A49.58,49.58,0,0,1,63.19,49q.64-3.12,1.55-6.13c.63-2,1.28-3.83,2-5.47a24.16,24.16,0,0,1,2-4c.67-1,1.27-1.6,1.79-1.69a1,1,0,0,1,1.08.68A4.51,4.51,0,0,1,72.18,34a9.53,9.53,0,0,1-.05,2.75,23.72,23.72,0,0,1-.81,3.59,31.44,31.44,0,0,1-1.46,3.82,24.75,24.75,0,0,1-2,3.58,12.25,12.25,0,0,1-2.32,2.66,12.7,12.7,0,0,0-.43,1.92c-.15.94-.26,2-.34,3.24A16.93,16.93,0,0,0,65,59a6.39,6.39,0,0,0,.89,2.56,1.75,1.75,0,0,0,1.86.8,3.9,3.9,0,0,0,2.21-1.31,6.67,6.67,0,0,0,1.6-2.67A4.92,4.92,0,0,1,69,56.55a6.67,6.67,0,0,1-1.16-2.8,4.52,4.52,0,0,1,.51-3.19,3,3,0,0,1,2.14-1.5,2.13,2.13,0,0,1,2,.66,4.52,4.52,0,0,1,1.09,2.57A10.39,10.39,0,0,1,73.61,55a12.76,12.76,0,0,1-.6,2.83h.48a3.43,3.43,0,0,0,.56,0,4,4,0,0,0,2.26-1,6.47,6.47,0,0,0,1.31-1.93,16.74,16.74,0,0,0,.8-2.11l.55.44a7.24,7.24,0,0,1-2.07,4A6.07,6.07,0,0,1,74,58.55q-.32,0-.63.06l-.65,0a11.25,11.25,0,0,1-2.15,3.48A5.5,5.5,0,0,1,67.18,64Zm4.61-6.45a10.27,10.27,0,0,0,.37-2,9.51,9.51,0,0,0-.09-2.13,4.94,4.94,0,0,0-.73-2.1c-.32-.49-.66-.7-1-.65s-.71.4-.94,1a4.22,4.22,0,0,0-.11,2.07A6.6,6.6,0,0,0,70,55.91,3.67,3.67,0,0,0,71.79,57.53ZM65.74,48.8a12.25,12.25,0,0,0,1.78-2.54,27.64,27.64,0,0,0,1.53-3.14c.44-1.12.83-2.2,1.15-3.25a22.31,22.31,0,0,0,.69-2.81A5.94,5.94,0,0,0,71,35.23c0-.13,0-.21-.07-.23a.13.13,0,0,0-.13-.06c-.21,0-.56.48-1,1.33a28.77,28.77,0,0,0-1.5,3.3c-.51,1.35-1,2.83-1.48,4.47A32,32,0,0,0,65.74,48.8Z"
            fill="#121318"
          />
          <path
            d="M83,60.62a4.85,4.85,0,0,1-3.08-.37,4.24,4.24,0,0,1-1.8-1.73,6.58,6.58,0,0,1-.85-2.29,4.14,4.14,0,0,1,0-1.86,2.5,2.5,0,0,1,.6-1.07,4.71,4.71,0,0,1,.79-.6,1.76,1.76,0,0,0,.54-.41,28.1,28.1,0,0,0,1.74-3A21.92,21.92,0,0,0,82.27,46l-.08-.47a3.34,3.34,0,0,1,.16-2.12,1.43,1.43,0,0,1,1-.8.66.66,0,0,1,.5.09A.5.5,0,0,1,84,43a2.49,2.49,0,0,1,0,.65,4.53,4.53,0,0,0,.06,1.12,9,9,0,0,0,1,2.75c.5.94,1,1.95,1.52,3a11.69,11.69,0,0,1,1.06,3.38,7.28,7.28,0,0,1,.08,1,4.78,4.78,0,0,1,0,.93,5.6,5.6,0,0,0,2.41-1.63,10.47,10.47,0,0,0,1.86-3.59l.42.33a6.9,6.9,0,0,1-1.73,3.66,7.49,7.49,0,0,1-3.06,1.89,4.56,4.56,0,0,1-1.62,2.79A5.93,5.93,0,0,1,83,60.62Zm-.13-1.39a3.59,3.59,0,0,0,1.21-.52,2.45,2.45,0,0,0,1-1.8,7.12,7.12,0,0,1-3.16-.84,8.22,8.22,0,0,1-2.2-1.67c-.57.11-.79.62-.65,1.51a4.67,4.67,0,0,0,1.24,2.51A2.84,2.84,0,0,0,82.89,59.23Zm2.27-3a6.08,6.08,0,0,0,0-.85c0-.29-.06-.63-.12-1a14.54,14.54,0,0,0-1-3.58c-.48-1.19-.91-2.31-1.29-3.35-.45,1.09-.94,2.15-1.48,3.17a20,20,0,0,1-1.54,2.54,7.51,7.51,0,0,0,2.18,2.08A6.82,6.82,0,0,0,85.16,56.25Z"
            fill="#121318"
          />
          <path
            d="M96.71,58.68a4.32,4.32,0,0,1-3.35-.77,5.19,5.19,0,0,1-1.85-3.58,10.47,10.47,0,0,1,.15-3.94,13.29,13.29,0,0,1,1.52-3.7,10.56,10.56,0,0,1,2.43-2.8,6.08,6.08,0,0,1,2.89-1.38,2.41,2.41,0,0,1,2.35.6,3.59,3.59,0,0,1,.9,1.88,3.65,3.65,0,0,1-.24,2.09,1.6,1.6,0,0,1-1.28,1.09A1.44,1.44,0,0,1,99,47.76a2.65,2.65,0,0,0,.82-1.42,4.62,4.62,0,0,0,.15-1.92,1.08,1.08,0,0,0-.31-.68c-.13-.17-.37-.22-.71-.17a3.54,3.54,0,0,0-2.06,1.29,11.49,11.49,0,0,0-1.71,2.77A18.08,18.08,0,0,0,94.1,51a9.19,9.19,0,0,0-.1,3.2,4.44,4.44,0,0,0,.94,2.16,2.42,2.42,0,0,0,2.44.66,6.46,6.46,0,0,0,3-1.36,10.55,10.55,0,0,0,2.6-2.94,13.1,13.1,0,0,0,1.62-4.13l.57.32a15.31,15.31,0,0,1-1.81,4.64,13.2,13.2,0,0,1-3,3.45A7.73,7.73,0,0,1,96.71,58.68Z"
            fill="#121318"
          />
          <path
            d="M111.86,55.1a3.1,3.1,0,0,1-2.24-.37,2.48,2.48,0,0,1-1-1.74,6.32,6.32,0,0,1,.17-2.56q.34-1.38.66-2.73a6.29,6.29,0,0,0,.19-2.41,2.24,2.24,0,0,0-1-1.73,13.28,13.28,0,0,0-1.64-.91c-.36,1.64-.76,3.21-1.2,4.7s-.81,2.63-1.12,3.4l-.46-.61c.26-.74.58-1.81,1-3.21a47.72,47.72,0,0,0,1.07-4.77,1.31,1.31,0,0,1-.54-.88,2.84,2.84,0,0,1,.37-2q.56-.84.87-.9a.41.41,0,0,1,.53.32,2.85,2.85,0,0,1,.17.54,2.83,2.83,0,0,1-.12.83,3.62,3.62,0,0,0-.09.7,1.38,1.38,0,0,0,.75.93c.47.22,1,.49,1.58.8a5.69,5.69,0,0,1,1.59,1.16,3.22,3.22,0,0,1,1,2,6.19,6.19,0,0,1-.13,2.48c-.19.86-.4,1.69-.63,2.48a5.18,5.18,0,0,0-.17,2c.08.47.24.77.51.89a1.45,1.45,0,0,0,.86.11,3.88,3.88,0,0,0,1.84-.89,10.31,10.31,0,0,0,1.73-1.81,16.43,16.43,0,0,0,1.4-2.15,9.67,9.67,0,0,0,.81-2.06l.57.31a15.12,15.12,0,0,1-1.54,3.59,12.48,12.48,0,0,1-2.5,3A5.93,5.93,0,0,1,111.86,55.1Z"
            fill="#121318"
          />
          <path
            d="M121.57,53.59a2.73,2.73,0,0,1-2.44-.63A3.74,3.74,0,0,1,118,50.75a10.8,10.8,0,0,1,0-2.33,29.85,29.85,0,0,1,.46-3,21.52,21.52,0,0,1,.81-3.07,9.93,9.93,0,0,1,1.24-2.42A2.32,2.32,0,0,1,122,38.81a.79.79,0,0,1,.62.14.73.73,0,0,1,.38.59,3.69,3.69,0,0,1-.39,1.63c-.29.77-.63,1.66-1,2.66a21.68,21.68,0,0,0-1,3.14,7.75,7.75,0,0,0-.14,3,2.76,2.76,0,0,0,.76,1.82,2.07,2.07,0,0,0,1.54.24,4.44,4.44,0,0,0,2.62-1.78,15.81,15.81,0,0,0,2.5-5.19l.42.34a11.38,11.38,0,0,1-2.5,5.71A6.87,6.87,0,0,1,121.57,53.59Zm1.91-18.77a2.17,2.17,0,0,1-1.19-.14,1.1,1.1,0,0,1-.63-.91,1.71,1.71,0,0,1,.73-1.56,3.49,3.49,0,0,1,1.83-.93,1.52,1.52,0,0,1,1,.12,1.17,1.17,0,0,1,.5.89,2,2,0,0,1-.6,1.59A2.66,2.66,0,0,1,123.48,34.82Z"
            fill="#121318"
          />
          <path
            d="M132.19,53.83a3.69,3.69,0,0,1-3.28-.86,6,6,0,0,1-1.55-3.47,19.38,19.38,0,0,1-.06-4.75,51.85,51.85,0,0,1,.9-5.87q.65-3.12,1.54-6.13c.63-2,1.29-3.83,2-5.47a23.37,23.37,0,0,1,2-4c.67-1,1.26-1.61,1.79-1.69s.8.16,1.07.68a4.74,4.74,0,0,1,.58,1.61,10,10,0,0,1-.06,2.75,23.72,23.72,0,0,1-.81,3.59A31.44,31.44,0,0,1,134.86,34a25.69,25.69,0,0,1-2,3.58,12.55,12.55,0,0,1-2.33,2.66,14.66,14.66,0,0,0-.42,1.92c-.15.94-.27,2-.35,3.24a17.66,17.66,0,0,0,.18,3.44,6.4,6.4,0,0,0,.88,2.56,1.76,1.76,0,0,0,1.86.8,4,4,0,0,0,2.22-1.31,6.79,6.79,0,0,0,1.6-2.67A4.9,4.9,0,0,1,134,46.4a6.72,6.72,0,0,1-1.16-2.8,4.52,4.52,0,0,1,.51-3.19,3,3,0,0,1,2.15-1.5,2.14,2.14,0,0,1,2,.66,4.51,4.51,0,0,1,1.08,2.57,10.82,10.82,0,0,1,.07,2.74,12.78,12.78,0,0,1-.61,2.83h.49a3.41,3.41,0,0,0,.55,0,4,4,0,0,0,2.26-1,6.21,6.21,0,0,0,1.31-1.94,17.82,17.82,0,0,0,.81-2.1L144,43a7.3,7.3,0,0,1-2.07,4,6.07,6.07,0,0,1-2.86,1.33c-.21,0-.42.05-.64.06l-.64,0A11.09,11.09,0,0,1,135.61,52,5.51,5.51,0,0,1,132.19,53.83Zm4.6-6.45a9.57,9.57,0,0,0,.37-2,8.46,8.46,0,0,0-.09-2.13,4.81,4.81,0,0,0-.73-2.1c-.32-.49-.66-.7-1-.65s-.7.4-.93,1a4.1,4.1,0,0,0-.12,2.07,6.6,6.6,0,0,0,.78,2.18A3.76,3.76,0,0,0,136.79,47.38Zm-6-8.73a12.67,12.67,0,0,0,1.79-2.54A27.58,27.58,0,0,0,134.05,33c.45-1.12.83-2.2,1.15-3.25a22.31,22.31,0,0,0,.69-2.81,6.37,6.37,0,0,0,.12-1.83.4.4,0,0,0-.08-.23.12.12,0,0,0-.13-.06c-.21,0-.56.48-1,1.33a28.77,28.77,0,0,0-1.5,3.3c-.52,1.34-1,2.83-1.48,4.47A32,32,0,0,0,130.74,38.65Z"
            fill="#121318"
          />
          <path
            d="M148.1,50.17a4.4,4.4,0,0,1-5.52-3.82,9.34,9.34,0,0,1,.29-3.87,16.25,16.25,0,0,1,1.78-4.07,13.93,13.93,0,0,1,2.83-3.31,6.69,6.69,0,0,1,3.34-1.65,3.05,3.05,0,0,1,1.72.22c.58.23.94.83,1.09,1.8a5,5,0,0,1-.46,3,9.07,9.07,0,0,1-2,2.69,14.14,14.14,0,0,1-2.89,2.18,13.13,13.13,0,0,1-3.26,1.32c0,.27,0,.52,0,.76s0,.47.06.68a5.38,5.38,0,0,0,.31,1.16,3.31,3.31,0,0,0,.68,1,2.41,2.41,0,0,0,1.06.6,3.92,3.92,0,0,0,1.58,0,6.8,6.8,0,0,0,3.2-1.47,13.4,13.4,0,0,0,2.67-3,16.63,16.63,0,0,0,1.86-3.84l.53.29a14.34,14.34,0,0,1-2.08,4.51,12,12,0,0,1-3.12,3.23A8.28,8.28,0,0,1,148.1,50.17Zm-3-6.31a18.68,18.68,0,0,0,2.24-1.4,17.84,17.84,0,0,0,2.3-2.05A9.09,9.09,0,0,0,151.4,38a4,4,0,0,0,.46-2.53,1.3,1.3,0,0,0-.27-.65.77.77,0,0,0-.7-.13A3.65,3.65,0,0,0,149,35.75,10.06,10.06,0,0,0,147.24,38a16.57,16.57,0,0,0-1.36,2.88A12.79,12.79,0,0,0,145.14,43.86Z"
            fill="#121318"
          />
          <path
            d="M148.72,206.17c-4.19.4-5.81,2-5.63,6.45a99.48,99.48,0,0,1-8.17,43.26c-4.94,11.7-13.44,19.51-24.76,25.44,2.7,2.84,5.52,5.47,7.93,8.42,9.45,11.56,13.61,25,13.55,39.76,0,1.43.49,3.36-1.75,3.5-1.79.11-1.87-1.64-2.25-2.87-.85-2.73-1.6-5.48-2.5-8.6-1.83,1.19-4.57.74-2.9,4.7,1.43,3.37-1.79,4-4.58,3.95-9.48-.17-18.94-.66-28.43-.2-12.69.6-25.38.78-38.08,1.11-8.7.22-11.77-3.5-10.28-12.31a32.87,32.87,0,0,1,2.87-9.11c3.69-7.54,8.23-14.56,12.73-21.61a18.46,18.46,0,0,1,8.35-7.13c-2.6-1.93-4.91-3.53-7.09-5.28C49.57,269.09,45.6,260.12,43,250.33c-6.06-23.12-6-45.89,3.43-68.24,8-18.78,29.2-30.69,49.48-27.6a69.36,69.36,0,0,1,15.26,3.57c2.44,1,4.66,2.08,8,.63,3.71-1.63,7.08,1.33,9.16,4.68a93.07,93.07,0,0,1,11.53,26.24,54.58,54.58,0,0,1,1.32,5.9c.51,3.7,1.3,6.39,6.1,4.11,1.81-.87,4,.19,5.51,1.76,4.24,4.32,2.06,15.36-3.57,17.49-1.27.48-3,1.52-4.18-.15-.95-1.33-.07-2.54.68-3.69,1.73-2.68,3.35-5.39,3-8.86ZM45.4,215.78c-.1,13.7,1.39,23.31,4,32.77,2,7.36,4.53,14.51,10.34,19.89,12.1,11.2,26.41,12.9,41.61,8.61,10.71-3,20-8.95,24.91-19.26,14.19-29.45,13.54-58.72-2.17-87.55a6.84,6.84,0,0,0-4.09-3.42c-11.14-3.81-22.53-6.27-34.34-5.26-12.89,1.11-23.81,5.88-30.47,17.85A81.24,81.24,0,0,0,45.4,215.78Zm55.38,94.4c-14.72,2.24-29.57,3.15-44,6.69-2.76.68-5.6,1.81-8.06-1.41-.9,3.21-1.69,5.94-2.45,8.68-.5,1.82.66,2,2,1.82.75-.08,1.71.12,1-1.36-2.28-4.45.93-5.14,4.17-5.6,3-.44,6-.73,9-1.14C75.37,316.09,88.51,315.2,100.78,310.18Zm-31.62-3.61c-.12-.44-.25-.87-.38-1.3-5.85,2.37-14-3.61-18.87,6.21Zm4.27,17.17c8.26-.68,16.6-.51,25.85-2.41C89.77,320.43,81.58,322,73.43,323.74Zm41.21-9.54c2.74.27,5.64,2.1,7.68.19,1-1-.46-3.29-1.61-5.22C119,311.32,116.67,312.48,114.64,314.2Z"
            fill="#121318"
          />
          <path
            d="M23,214.33c.51-4.1,2.27-7.39,6.27-9a6.29,6.29,0,0,1,8.57,4.59c.45,2,.66,4.15-2.54,2.57-2.08-1-3.93-1-5.46.76-1.77,2.11-.13,3.71,1,5.33a6.92,6.92,0,0,1,.88,1.23c.84,2.12,4.88,3.84,2.54,6-1.94,1.84-4.39-1.39-6.43-2.61A10.45,10.45,0,0,1,23,214.33Z"
            fill="#121318"
          />
          <path
            d="M96.76,182.31a24.89,24.89,0,0,0,8.42-9.28c1-2,1.88-4.89,4.92-4.08,3.47.91,2.27,4,2.11,6.37a39,39,0,0,1-.71,3.88,32.58,32.58,0,0,1,3.13-2.47c1.61-.95,2.88-3.68,5.09-2.08,1.89,1.35.12,3.49-.31,5.12-1.49,5.6-12,14-17.09,11.89-5.39-2.22-8.35.63-12.32,2-3.36,1.14-4.53,0-5-4.74-3.83,1.87-7.65,3.58-11.94,3.49-1.85,0-3.82.13-4.88-1.88s.4-3.12,1.24-4.54c2.9-4.92,7.85-9,6.11-15.81-.39-1.55.85-2.87,2.73-2.91a2.88,2.88,0,0,1,3.11,2.7,17.07,17.07,0,0,1-1.47,10.24c-.71,1.47-1.52,2.88-2.63,4.95A23.45,23.45,0,0,0,91.8,172.79c1-2,1.84-4.93,4.94-3.92,3.42,1.11,2.23,3.95,1.64,6.4-.47,1.92-.89,3.86-1.62,7Z"
            fill="#121318"
          />
          <path
            d="M94.64,261.55c-9.82-.64-17.44-2.77-23.26-9.2-1.79-1.95-3.69-4.2-1-6.45s4.67-.27,6.08,2.13c2.36,4,6.39,5.41,10.55,6.13,12.1,2.07,25.06-4,27.15-14.65.31-1.56,1.07-3.16,2.9-2.84,2.13.37,2,2.29,2,4,0,9.57-6.19,17.16-16.43,19.59-3.09.73-6.27,1.48-8,1.25Z"
            fill="#121318"
          />
          <path
            d="M74.41,211.21c-.34,2.85-.64,5.7-1.93,8.33-.86,1.74-2.1,3.21-4.14,3.32s-3.42-1.36-4.34-3c-2.84-5.18-2.16-10.81-1.37-16.23.51-3.47,6.63-4.19,9.14-1.35,1.73,2,2.83,5.67,2.64,9Z"
            fill="#121318"
          />
          <path
            d="M119.45,207.7a17,17,0,0,1-2.39,10.51c-1.1,1.83-2.72,3.17-5,2.68a4.8,4.8,0,0,1-4-4.55,39.07,39.07,0,0,1,.53-11c.55-3.1,1.9-5.95,5.81-5.74,3.69.21,4.38,3.19,5,6A6.66,6.66,0,0,1,119.45,207.7Z"
            fill="#121318"
          />
        </svg>
      </div>

      <div class="col-lg-8 col-xl-7">
        <div
          class="app-newsletter__title h4 mb-3 md:mb-6"
          v-html="$t('component.newsletter.title')"
        ></div>

        <p class="mb-6 md:mb-10">
          {{ $t('component.newsletter.description') }}
        </p>

        <form class="app-newsletter__form" @submit="onSubmit">
          <AppTextField
            v-model="name"
            v-bind="nameAttrs"
            :error="!!errors.name"
            class="mb-2.5"
            :placeholder="$t('component.newsletter.label.name')"
            full-width
          />

          <span class="app-newsletter__input-divider"></span>

          <AppTextField
            v-model="email"
            v-bind="emailAttrs"
            :error="!!errors.email"
            class="mb-2.5"
            :placeholder="$t('component.newsletter.label.email')"
            full-width
          />

          <span class="app-newsletter__button-divider"></span>

          <AppButton type="submit" :loading="isSubmitting">
            {{ $t('component.newsletter.button') }}
          </AppButton>
        </form>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-newsletter__illustration {
  display: none;

  @include media-breakpoint-up(lg) {
    display: block;
    width: 11.25rem;
    margin: 0 auto;
  }
}

.app-newsletter__title {
  b {
    display: block;
    font-family: $font-barlow-condensed;
    font-size: 3.5rem;
    font-weight: 600;
    line-height: 1.1;
    text-transform: uppercase;
  }

  @include media-breakpoint-up(md) {
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 2;

    b {
      font-size: 7.5rem;
      line-height: 0.9;
    }
  }
}

.app-newsletter__form {
  @include media-breakpoint-up(md) {
    display: flex;
    background-color: $gray-100;
    border: 1px solid $black;
    border-radius: 0.125rem;
    overflow: hidden;

    .app-text-field {
      min-height: 100%;
      margin: 0;
      border: 0;
      padding-top: 0;
      padding-bottom: 0;
      background-color: transparent;
    }

    .app-button {
      border-radius: 0;
    }
  }
}

.app-newsletter__input-divider {
  display: none;

  @include media-breakpoint-up(md) {
    flex: 0 0 auto;
    align-self: center;
    display: block;
    width: 1px;
    height: 1.25rem;
    background-color: $gray-400;
  }
}

.app-newsletter__button-divider {
  display: none;

  @include media-breakpoint-up(md) {
    flex: 0 0 auto;
    display: block;
    width: 1px;
    background-color: $black;
  }
}

.app-text-field--error {
  color: $signal-error-red;
}
</style>
